import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Loader2, Calendar, ArrowRight, AlertCircle, CheckCircle } from 'lucide-react';
import API_CONFIG from '../config/api';

interface Mesure {
  minutes: number;
  km: number;
}

/** Suggestion renvoyée par GET /api/optimisations */
interface Suggestion {
  cle: string;
  client: string;
  de: string;
  vers: string;
  resume: string;
  // Absents tant que le serveur n'est pas à jour : la carte se contente alors du résumé
  heure?: string | null;
  minutesEconomisees?: number;
  kmEconomises?: number;
  journeeLiberee?: boolean;
  avant?: Mesure;
  apres?: Mesure;
  detourAvant?: Mesure;
  detourApres?: Mesure;
  plusProcheAvant?: Mesure | null;
  plusProcheApres?: Mesure | null;
}

const jourCourt = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' });

const duree = (minutes: number) =>
  minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;

const km = (valeur: number) => `${valeur < 10 ? valeur.toFixed(1) : Math.round(valeur)} km`;

/** Une barre par journée : la route que ce client y ajoute. La partie hachurée est ce qu'on gagne. */
const Barre = ({ jour, mesure, reference, couleur }: { jour: string; mesure: Mesure; reference: number; couleur: 'avant' | 'apres' }) => {
  const part = reference > 0 ? Math.min(100, Math.max(3, (mesure.minutes / reference) * 100)) : 100;
  return (
    <>
      <span className={`whitespace-nowrap text-[11px] ${couleur === 'avant' ? 'text-amber-400' : 'text-cyan-300'}`}>{jour}</span>
      <div className="flex h-3 overflow-hidden rounded-full bg-gray-800/80">
        <div className={`h-full rounded-full ${couleur === 'avant' ? 'bg-amber-500' : 'bg-cyan-400'}`} style={{ width: `${part}%` }} />
        {couleur === 'apres' && part < 100 && (
          <div
            className="h-full flex-1 rounded-r-full"
            style={{ backgroundImage: 'repeating-linear-gradient(135deg, rgba(52,211,153,0.45) 0 4px, transparent 4px 8px)' }}
          />
        )}
      </div>
      <span className="whitespace-nowrap text-right text-xs tabular-nums text-gray-100 sm:text-sm">
        <span className="font-semibold">{duree(mesure.minutes)}</span>
        <span className="text-[11px] text-gray-500"> · {km(mesure.km)}</span>
      </span>
    </>
  );
};

const CarteSuggestion = ({ s }: { s: Suggestion }) => {
  const [agrandie, setAgrandie] = useState<'avant' | 'apres' | null>(null);
  const image = (vue: 'avant' | 'apres') =>
    `${API_CONFIG.endpoints.optimisationsCarte}?cle=${encodeURIComponent(s.cle)}&vue=${vue}&v=5`;

  return (
    <div className="rounded-xl border border-indigo-500/30 bg-gradient-to-br from-gray-900/60 to-gray-800/50 p-4">
      {/* Qui, quand, combien */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-semibold text-gray-100">{s.client}</p>
          <p className="text-xs text-gray-400">
            <span className="whitespace-nowrap">{jourCourt(s.de)}</span>{' '}
            <ArrowRight className="inline h-3 w-3 text-gray-500" />{' '}
            <span className="whitespace-nowrap text-cyan-200">{jourCourt(s.vers)}</span>
            {s.heure !== undefined && <span className="whitespace-nowrap"> · {s.heure ?? 'Toute la journée'}</span>}
          </p>
        </div>
        {s.minutesEconomisees !== undefined && (
          <div className="shrink-0 text-right">
            <p className="text-lg font-bold leading-none text-emerald-300">−{duree(s.minutesEconomisees)}</p>
            {s.kmEconomises !== undefined && <p className="mt-1 text-[11px] text-emerald-200/70">−{km(s.kmEconomises)}</p>}
          </div>
        )}
      </div>

      {s.avant && s.apres ? (
        <>
          {/* Ce que ce client coûte en route, sur sa journée actuelle puis sur la journée proposée */}
          <div className="mt-3 rounded-lg bg-gray-950/40 px-3 py-2.5">
            <p className="mb-1.5 text-[11px] text-gray-500">
              {s.detourAvant && s.detourApres ? 'Route ajoutée par ce client' : 'Route des deux journées'}
            </p>
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2.5 gap-y-1.5">
              <Barre jour={jourCourt(s.de)} mesure={s.detourAvant ?? s.avant} reference={(s.detourAvant ?? s.avant).minutes} couleur="avant" />
              <Barre jour={jourCourt(s.vers)} mesure={s.detourApres ?? s.apres} reference={(s.detourAvant ?? s.avant).minutes} couleur="apres" />
            </div>
            {s.detourAvant && s.detourApres && (
              <p className="mt-2 text-[11px] text-gray-500">
                Total des deux journées : {duree(s.avant.minutes)} <ArrowRight className="inline h-3 w-3" />{' '}
                <span className="text-gray-300">{duree(s.apres.minutes)}</span>
              </p>
            )}
          </div>

          {/* Aperçus : sa journée actuelle, puis la journée proposée une fois le RDV ajouté */}
          <div className="mt-3 grid grid-cols-2 gap-2">
            {(['avant', 'apres'] as const).map((vue) => {
              const voisin = vue === 'avant' ? s.plusProcheAvant : s.plusProcheApres;
              return (
                <div key={vue}>
                  <p className="mb-1 whitespace-nowrap text-[11px] font-medium">
                    <span className="uppercase tracking-wide text-gray-400">{vue === 'avant' ? 'Avant' : 'Après'}</span>
                    <span className={vue === 'avant' ? 'text-amber-400' : 'text-cyan-300'}>
                      {' '}
                      ★ {jourCourt(vue === 'avant' ? s.de : s.vers)}
                    </span>
                  </p>
                  <button
                    onClick={() => setAgrandie(agrandie === vue ? null : vue)}
                    className={`block aspect-[32/22] w-full overflow-hidden rounded-lg border bg-gray-900 p-0 ${
                      vue === 'avant' ? 'border-amber-500/40' : 'border-cyan-400/40'
                    }`}
                  >
                    <img src={image(vue)} alt={vue === 'avant' ? 'Journée actuelle' : 'Journée proposée'} loading="lazy" className="h-full w-full object-cover" />
                  </button>
                  <p className="mt-1 text-center text-[11px] text-gray-400">
                    {voisin ? `Voisin le plus proche à ${duree(voisin.minutes)}` : vue === 'avant' ? 'Seul ce jour-là' : '—'}
                  </p>
                </div>
              );
            })}
          </div>
          {agrandie && (
            <button onClick={() => setAgrandie(null)} className="mt-2 block w-full overflow-hidden rounded-lg border border-gray-700/60 p-0">
              <img src={image(agrandie)} alt="Aperçu agrandi" className="w-full" />
            </button>
          )}
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
            <span>
              <span className="text-gray-300">★</span> {s.client}
            </span>
            <span>Trait épais : la route pour aller chez lui</span>
            {s.journeeLiberee && <span className="text-amber-300">sortie du {jourCourt(s.de)} évitée</span>}
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-gray-400">{s.resume}</p>
      )}

      {/* Planning */}
      <div className="mt-3 flex gap-2">
        <Link
          to={`/schedule?date=${s.vers}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-cyan-400/40 bg-cyan-500/15 px-2 py-1.5 text-xs text-cyan-100 transition-all hover:bg-cyan-500/25"
        >
          <Calendar className="h-3.5 w-3.5" />
          {jourCourt(s.vers)}
        </Link>
        <Link
          to={`/schedule?date=${s.de}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-600/40 bg-gray-700/30 px-2 py-1.5 text-xs text-gray-300 transition-all hover:bg-gray-700/50"
        >
          <Calendar className="h-3.5 w-3.5" />
          {jourCourt(s.de)}
        </Link>
      </div>
    </div>
  );
};

const Optimisations = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[] | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [miseAJour, setMiseAJour] = useState<Date | null>(null);

  // Calculée à l'ouverture : la liste reflète la sédule du moment, et une
  // suggestion disparaît dès que le RDV a été déplacé.
  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const resp = await fetch(API_CONFIG.endpoints.optimisations);
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Impossible de calculer les optimisations');
      setSuggestions(data.suggestions || []);
      setMiseAJour(new Date());
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  return (
    <div className="min-h-screen py-6">
      <div className="mx-auto max-w-2xl space-y-4 px-1 sm:px-6">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="bg-gradient-to-r from-cyan-300 via-indigo-300 to-purple-300 bg-clip-text text-2xl font-bold text-transparent">
              Optimisations
            </h1>
            <p className="text-xs text-gray-500">
              {chargement
                ? 'Calcul des tournées…'
                : suggestions
                  ? `${suggestions.length} déplacement${suggestions.length > 1 ? 's' : ''} qui réduisent la route`
                  : ''}
              {miseAJour && !chargement && (
                <> · {miseAJour.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}</>
              )}
            </p>
          </div>
          <button
            onClick={charger}
            disabled={chargement}
            aria-label="Actualiser"
            className="rounded-lg border border-cyan-400/40 bg-cyan-500/10 p-2 text-cyan-200 transition-all hover:bg-cyan-500/20 disabled:opacity-50"
          >
            {chargement ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </button>
        </div>

        {erreur && (
          <div className="flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{erreur}</span>
          </div>
        )}

        {chargement && !suggestions && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-indigo-500/30 p-8 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin text-cyan-300" />
            Calcul des tournées…
          </div>
        )}

        {suggestions && suggestions.length === 0 && !erreur && (
          <div className="flex flex-col items-center gap-1 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
            <CheckCircle className="h-7 w-7 text-emerald-300" />
            <p className="text-sm text-gray-200">Rien à optimiser pour l'instant.</p>
            <p className="text-xs text-gray-500">Tu recevras une notification dès qu'il y a quelque chose.</p>
          </div>
        )}

        {suggestions?.map((s) => <CarteSuggestion key={s.cle} s={s} />)}

        {suggestions && suggestions.length > 0 && (
          <p className="text-center text-[11px] text-gray-600">
            Rien n'est déplacé automatiquement : préviens le client avant de changer son RDV.
          </p>
        )}
      </div>
    </div>
  );
};

export default Optimisations;
